/**
 * Formate la réponse de `POST .../check_decomposition/` (hoortrade-bo) pour l’UI.
 *
 * Contrat BO : {
 *   best_candidate: Record<pcsRef, stiRef | pcsRef>,
 *   success_message: Record<pcsRef, string>,
 *   error_message: Record<pcsRef, string>,
 *   common_decompositions: Record<stiRef, { quantity, pcs_list, pcs_details, message }>,
 *   pending_decomposition: Record<pcsRef, { decomposition_references, available, quantity, in_stock, breakdown, message }>,
 * }
 *
 * @param {object | null | undefined} raw
 * @param {string[]} [orderedReferences] ordre d'affichage (ex. lignes saisies)
 */
export function formatDecompositionResponse(raw, orderedReferences = []) {
  if (raw == null || typeof raw !== 'object') {
    return {
      ok: false,
      serverError: true,
      pcsRows: [],
      commonDecompositions: [],
      summary: { errorCount: 0, warnCount: 0, successCount: 0, infoCount: 0 },
    };
  }

  const best_candidate = raw.best_candidate ?? {};
  const success_message = raw.success_message ?? {};
  const error_message = raw.error_message ?? {};
  const common_decompositions = raw.common_decompositions ?? {};
  const pending_decomposition = raw.pending_decomposition ?? {};
  const decomposition_units = raw.decomposition_units ?? {};

  const refSet = new Set([
    ...orderedReferences,
    ...Object.keys(best_candidate),
    ...Object.keys(success_message),
    ...Object.keys(error_message),
    ...Object.keys(pending_decomposition),
  ]);
  const refs = orderedReferences.length
    ? [...orderedReferences.filter((r) => refSet.has(r)), ...[...refSet].filter((r) => !orderedReferences.includes(r))]
    : [...refSet];

  const pcsRows = refs.map((ref) => {
    // Le BO renvoie best_candidate[ref] === ref quand la PCS est déjà en stock
    // (cf. contrat `stiRef | pcsRef`) : ce n'est pas un produit à décomposer.
    const rawCandidate = best_candidate[ref] ?? null;
    const inStock = rawCandidate != null && rawCandidate === ref;
    const candidate = inStock ? null : rawCandidate;
    const successMessage = success_message[ref] ?? null;
    const errorMessage = error_message[ref] ?? null;
    const pendingRaw = pending_decomposition[ref] ?? null;
    const pendingDecomposition = pendingRaw
      ? {
          decompositionReferences: Array.isArray(pendingRaw.decomposition_references)
            ? pendingRaw.decomposition_references
            : [],
          available: pendingRaw.available ?? 0,
          quantity: pendingRaw.quantity ?? 0,
          inStock: pendingRaw.in_stock ?? 0,
          breakdown: Array.isArray(pendingRaw.breakdown)
            ? pendingRaw.breakdown.map((entry) => ({
                decomposition: entry?.decomposition ?? '',
                quantity: entry?.quantity ?? 0,
              }))
            : [],
          message: pendingRaw.message ?? '',
        }
      : null;
    // Nombre d'unités du candidat (STI/COM) à décomposer pour couvrir le besoin
    // (résiduel en cas de surplus partiel). Défaut 1 si non fourni par le BO.
    const decompositionUnits = decomposition_units[ref] ?? null;
    // Cas partiel : une décompo en cours couvre une partie (réservation) ET il faut
    // décomposer le reste (candidat). Les deux sont alors présents pour la même PCS.
    const isPartial = Boolean(candidate && pendingDecomposition);

    /** @type {'error' | 'warning' | 'success' | 'info'} */
    let variant = 'info';
    if (errorMessage && !candidate) {
      variant = 'error';
    } else if (errorMessage && candidate) {
      variant = 'warning';
    } else if (pendingDecomposition) {
      // Couvert (totalement ou partiellement) par une décomposition en cours : info.
      variant = 'info';
    } else if (successMessage || candidate) {
      variant = 'success';
    }

    return {
      reference: ref,
      candidate,
      inStock,
      decompositionUnits,
      isPartial,
      successMessage,
      errorMessage,
      pendingDecomposition,
      variant,
    };
  });

  const commonDecompositions = Object.entries(common_decompositions).map(
    ([internalProductRef, data]) => ({
      internalProductRef,
      quantity: data?.quantity ?? 0,
      pcsList: Array.isArray(data?.pcs_list) ? data.pcs_list : [],
      pcsDetails: Array.isArray(data?.pcs_details) ? data.pcs_details : [],
      message: data?.message ?? '',
    }),
  );

  const errorCount = pcsRows.filter((r) => r.variant === 'error').length;
  const warnCount = pcsRows.filter((r) => r.variant === 'warning').length;
  const successCount = pcsRows.filter((r) => r.variant === 'success').length;
  const infoCount = pcsRows.filter((r) => r.variant === 'info').length;
  const pendingCount = pcsRows.filter((r) => r.pendingDecomposition).length;

  return {
    ok: errorCount === 0,
    serverError: false,
    pcsRows,
    commonDecompositions,
    summary: { errorCount, warnCount, successCount, infoCount, pendingCount },
  };
}
